import type { TFunction } from 'i18next';

/**
 * Shape of an RTK Query error whose payload follows the backend API Response
 * Standard (see root CLAUDE.md → API Response Standard). Every error response
 * carries a 4-digit `message_code` used as an i18n key under `messages.*`.
 *
 * `detail` is typed `unknown` on purpose: FastAPI's *own* 422 handler bypasses the
 * standard and returns an array of validation objects, so anything reading it has
 * to normalise before rendering (see `getErrorDetail`).
 */
interface ApiErrorPayload {
  data?: { message_code?: number; message?: string; detail?: unknown };
}

/** One entry of FastAPI's default 422 body: `{"detail": [{loc, msg, type}, …]}`. */
interface ValidationError {
  loc?: unknown[];
  msg?: unknown;
  type?: unknown;
}

/** Request-part prefixes FastAPI puts first in `loc`; noise to a user, so drop them. */
const LOC_PREFIXES = new Set(['body', 'query', 'path', 'header', 'cookie']);

/** `["body", "primary_contact", "email"]` → `"primary_contact.email"` (empty when unusable). */
const locLabel = (loc: unknown): string => {
  if (!Array.isArray(loc)) return '';
  const parts = loc.filter((p): p is string | number => typeof p === 'string' || typeof p === 'number');
  const trimmed = parts.length && LOC_PREFIXES.has(String(parts[0])) ? parts.slice(1) : parts;
  return trimmed.join('.');
};

const isValidationError = (v: unknown): v is ValidationError =>
  typeof v === 'object' && v !== null && typeof (v as ValidationError).msg === 'string';

/** `"email: value is not a valid email address"` — the field prefix only when `loc` names one. */
const formatValidationError = (e: ValidationError): string => {
  const field = locLabel(e.loc);
  return field ? `${field}: ${e.msg}` : String(e.msg);
};

/**
 * Normalise an API error `detail` of unknown shape to a renderable string.
 *
 * Handles the three shapes the backend can produce:
 *  - a plain string (an `AppException` detail) — returned as-is;
 *  - an array of FastAPI validation objects — each `msg` prefixed with its field
 *    from `loc`, joined into one line with `; ` so adjacent field errors stay
 *    visually separate (a bare space runs one message into the next field's label);
 *  - anything else — `undefined`, so callers fall back to their own message.
 *
 * Never returns a non-string: rendering `detail` raw is what crashed RegisterPage
 * with "Objects are not valid as a React child" on a 422.
 */
export const detailToMessage = (detail: unknown): string | undefined => {
  if (typeof detail === 'string') return detail.trim() || undefined;

  if (Array.isArray(detail)) {
    const messages = detail.filter(isValidationError).map(formatValidationError);
    return messages.length ? messages.join('; ') : undefined;
  }

  if (isValidationError(detail)) return formatValidationError(detail);

  return undefined;
};

/** Extract the backend `message_code` from a caught RTK Query error, if present. */
export const getMessageCode = (err: unknown): number | undefined =>
  (err as ApiErrorPayload)?.data?.message_code;

/** Extract the backend `detail` from a caught RTK Query error as a renderable string, if
 *  present — carries case-specific text (e.g. a governance gate's own failure_message, or a
 *  422's field errors) that the generic `message` for the message_code doesn't include. */
export const getErrorDetail = (err: unknown): string | undefined =>
  detailToMessage((err as ApiErrorPayload)?.data?.detail);

/**
 * Resolve a caught API error to a human-readable, localized message.
 *
 * Prefers the backend `message_code` (`t('messages.<code>')`); falls back to the
 * provided default string when no code is present. This is the standard way to
 * surface API errors in the UI — never display raw error objects or hardcoded
 * English when a message_code is available.
 */
export const apiErrorMessage = (
  err: unknown,
  t: TFunction,
  fallback: string
): string => {
  const code = getMessageCode(err);
  if (code != null) {
    const key = `messages.${code}`;
    const translated = t(key);
    // i18next returns the key unchanged when there's no entry for it.
    if (translated !== key) return translated;
  }
  return fallback;
};
